import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Package,
  Truck,
  CheckCircle2,
  Loader2,
  Search,
  XCircle,
  ShieldCheck,
  RotateCcw,
  MessageCircle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { apiClient, trackOrder } from "../lib/api";
import { toast } from "sonner";
import { BRAND } from "../data/content";

const STATUS_STEPS = [
  { key: "placed", label: "Order placed", icon: Package },
  { key: "confirmed", label: "Confirmed", icon: ShieldCheck },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

const CANCELLABLE = new Set(["placed", "confirmed", "processing"]);
const RETURN_ELIGIBLE = new Set(["delivered"]);
const NEGATIVE_STATES = new Set(["cancelled", "return", "refund_initiated", "refunded"]);

const STATUS_LABELS = {
  placed: "PLACED",
  confirmed: "CONFIRMED",
  processing: "PROCESSING",
  shipped: "SHIPPED",
  delivered: "DELIVERED",
  cancelled: "CANCELLED",
  return: "RETURN REQUESTED",
  refund_initiated: "REFUND INITIATED",
  refunded: "REFUNDED",
};

const StatusBadge = ({ status }) => {
  const colors = {
    placed: "bg-[#FFF5E1] text-[#F39C12]",
    confirmed: "bg-blue-50 text-blue-700",
    processing: "bg-blue-50 text-blue-700",
    shipped: "bg-indigo-50 text-indigo-700",
    delivered: "bg-emerald-50 text-emerald-700",
    cancelled: "bg-red-50 text-red-700",
    return: "bg-orange-50 text-orange-700",
    refund_initiated: "bg-amber-50 text-amber-700",
    refunded: "bg-emerald-50 text-emerald-700",
  };
  return (
    <span
      data-testid="track-status-badge"
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
        colors[status] || colors.placed
      }`}
    >
      {STATUS_LABELS[status] || (status || "placed").toUpperCase()}
    </span>
  );
};

const ReturnDialog = ({ order, onReturned }) => {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const confirm = async () => {
    if (mobile.replace(/\D/g, "").length < 10) {
      toast.error("Please enter the mobile number used at checkout");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post(
        `/orders/${order.order_number}/return`,
        { mobile }
      );
      onReturned?.(data);
      toast.success("Return requested. Opening WhatsApp…");
      setOpen(false);
      setMobile("");
      const waText = encodeURIComponent(
        `I want to return the order ${order.order_number}.`
      );
      window.open(
        `https://wa.me/${BRAND.phoneDigits}?text=${waText}`,
        "_blank",
        "noopener,noreferrer"
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not raise return");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="return-order-btn"
          variant="outline"
          className="rounded-full h-11 px-5 border-[#D63031]/40 text-[#D63031] hover:bg-[#D63031] hover:text-white"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Return this order
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Return order #{order.order_number}</DialogTitle>
          <DialogDescription className="text-[#2C3E50]/85 leading-relaxed">
            Please courier the product to:
            <span
              data-testid="return-address"
              className="block mt-2 p-3 rounded-lg bg-[#FFF5E1] border border-[#F39C12]/30 text-[#2C3E50] font-medium"
            >
              {BRAND.returnAddress}
            </span>
            <span className="block mt-3 text-sm text-[#7F8C8D]">
              Parcel cost and complete refund will be initiated post confirmation
              of quality and claim.
            </span>
          </DialogDescription>
        </DialogHeader>
        <div>
          <p className="text-sm font-medium text-[#2C3E50] mb-1">
            Mobile used at checkout
          </p>
          <Input
            data-testid="return-mobile-input"
            value={mobile}
            onChange={(e) => setMobile(e.target.value.replace(/[^\d+ ]/g, ""))}
            placeholder="10-digit mobile number"
            className="h-11"
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            data-testid="return-cancel-btn"
          >
            Keep order
          </Button>
          <Button
            data-testid="return-confirm-btn"
            onClick={confirm}
            disabled={submitting}
            className="cta-red rounded-full"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <MessageCircle className="w-4 h-4 mr-2" />
            )}
            Confirm & chat on WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TrackOrder = () => {
  const [q, setQ] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelMobile, setCancelMobile] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setLoading(true);
    setOrder(null);
    try {
      const data = await trackOrder(q.trim());
      setOrder(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "No order found";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!order) return;
    setCancelling(true);
    try {
      const { data } = await apiClient.post(
        `/orders/${order.order_number}/cancel`,
        { mobile: cancelMobile }
      );
      setOrder(data);
      setCancelMobile("");
      toast.success("Order cancelled successfully");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel order");
    } finally {
      setCancelling(false);
    }
  };

  const currentStepIdx = order
    ? STATUS_STEPS.findIndex((s) => s.key === order.status)
    : -1;
  const isNegative = order && NEGATIVE_STATES.has(order.status);
  const canCancel = order && CANCELLABLE.has(order.status);
  const canReturn = order && RETURN_ELIGIBLE.has(order.status);

  return (
    <div className="font-body">
      <section className="bg-[#FBF7F0] py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm uppercase tracking-[0.2em] text-[#D63031] font-semibold mb-3">
            Order tracking
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold">
            Track your order.
          </h1>
          <p className="mt-4 text-[#2C3E50]/80">
            Enter your order number or registered mobile number.
          </p>

          <form
            onSubmit={handleSearch}
            className="mt-8 flex flex-col sm:flex-row gap-3 bg-white border border-[#F39C12]/20 rounded-full p-2 sm:pl-5"
          >
            <Input
              data-testid="track-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. GR2607281234 or 9876543210"
              className="flex-1 border-0 focus-visible:ring-0 bg-transparent h-12 text-base"
            />
            <Button
              data-testid="track-submit"
              type="submit"
              disabled={loading}
              className="cta-red rounded-full h-12 px-7 font-semibold"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Track Order
            </Button>
          </form>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {!order && !loading && (
            <div className="bg-white border border-[#F39C12]/20 rounded-2xl p-8 text-center">
              <p className="text-[#7F8C8D]">
                Your order details and timeline will appear here.
              </p>
            </div>
          )}

          {order && (
            <div
              className="bg-white border border-[#F39C12]/20 rounded-2xl p-7"
              data-testid="track-order-result"
            >
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#7F8C8D]">
                    Order
                  </p>
                  <h2 className="font-display text-2xl font-bold text-[#2C3E50]">
                    #{order.order_number}
                  </h2>
                  <p className="text-sm text-[#7F8C8D] mt-1">
                    Placed on{" "}
                    {new Date(order.created_at).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <StatusBadge status={order.status} />
              </div>

              <div className="mt-8">
                <ol className="grid grid-cols-4 gap-2">
                  {STATUS_STEPS.map((s, idx) => {
                    const Icon = s.icon;
                    const reached = !isNegative && idx <= Math.max(currentStepIdx, 0);
                    return (
                      <li key={s.key} className="text-center">
                        <div
                          className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center ${
                            isNegative
                              ? "bg-[#D63031]/15 text-[#D63031]"
                              : reached
                              ? "bg-[#27AE60] text-white"
                              : "bg-[#FFF5E1] text-[#F39C12]"
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <p
                          className={`mt-2 text-xs font-medium ${
                            reached ? "text-[#2C3E50]" : "text-[#7F8C8D]"
                          }`}
                        >
                          {s.label}
                        </p>
                      </li>
                    );
                  })}
                </ol>
              </div>

              {order.status === "shipped" && (
                <div
                  data-testid="shipped-info-banner"
                  className="mt-6 flex items-start gap-2 p-4 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-900 text-sm leading-relaxed"
                >
                  <Truck className="w-5 h-5 shrink-0 mt-0.5" />
                  <div>
                    Once the order has been shipped, it can not be cancelled.
                    Post delivery you can return or replace the order. Please
                    visit our{" "}
                    <Link
                      to="/returns"
                      className="underline font-semibold hover:text-indigo-700"
                    >
                      Return &amp; Refund Policy
                    </Link>{" "}
                    page to know more. Still if you wish to connect to our
                    team, kindly contact at{" "}
                    <a
                      href={`tel:+919664527720`}
                      className="underline font-semibold hover:text-indigo-700"
                    >
                      9664527720
                    </a>
                    .
                  </div>
                </div>
              )}

              {order.status === "cancelled" && (
                <div className="mt-6 flex items-center gap-2 p-3 rounded-xl bg-[#D63031]/8 border border-[#D63031]/25 text-[#D63031] text-sm">
                  <XCircle className="w-5 h-5 shrink-0" />
                  <span>
                    This order was cancelled. If you did not request this, please contact us.
                  </span>
                </div>
              )}

              {order.status === "return" && (
                <div
                  data-testid="return-status-banner"
                  className="mt-6 flex items-start gap-2 p-3 rounded-xl bg-orange-50 border border-orange-200 text-orange-800 text-sm"
                >
                  <RotateCcw className="w-5 h-5 shrink-0 mt-0.5" />
                  <span>
                    Return requested. Please courier the product to <b>{BRAND.returnAddress}</b>.
                    Refund will be initiated after we receive and inspect the parcel.
                  </span>
                </div>
              )}

              {order.status === "refund_initiated" && (
                <div className="mt-6 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  Refund initiated — you&apos;ll receive an update shortly.
                </div>
              )}

              {order.status === "refunded" && (
                <div className="mt-6 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                  Refund complete. Thanks for shopping with Gruham.
                </div>
              )}

              {(canCancel || canReturn) && (
                <div className="mt-6 p-4 rounded-xl bg-[#FFF5E1] border border-[#F39C12]/30 space-y-3">
                  <p className="text-sm font-semibold text-[#2C3E50]">
                    Order actions
                  </p>
                  <div className="flex items-center flex-wrap gap-3">
                    {canReturn && (
                      <ReturnDialog
                        order={order}
                        onReturned={(o) => setOrder(o)}
                      />
                    )}
                    {canCancel && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            data-testid="cancel-order-btn"
                            variant="outline"
                            className="rounded-full h-11 px-5 border-[#D63031]/40 text-[#D63031] hover:bg-[#D63031] hover:text-white"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Cancel this order
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Cancel order #{order.order_number}?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Enter the mobile number used at checkout to confirm. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <div className="my-2">
                            <Input
                              data-testid="cancel-mobile-input"
                              value={cancelMobile}
                              onChange={(e) =>
                                setCancelMobile(
                                  e.target.value.replace(/[^\d+ ]/g, "")
                                )
                              }
                              placeholder="10-digit mobile number"
                              className="h-11"
                            />
                          </div>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="cancel-dialog-close">
                              Keep order
                            </AlertDialogCancel>
                            <AlertDialogAction
                              data-testid="confirm-cancel-btn"
                              onClick={handleCancel}
                              disabled={
                                cancelling ||
                                cancelMobile.replace(/\D/g, "").length < 10
                              }
                              className="bg-[#D63031] hover:bg-[#b82828]"
                            >
                              {cancelling ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : null}
                              Yes, cancel
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-8 grid md:grid-cols-2 gap-5">
                <div className="bg-[#FFFBF5] rounded-xl p-5 border border-[#F39C12]/15">
                  <p className="text-xs uppercase tracking-widest text-[#F39C12] font-semibold mb-2">
                    Shipping To
                  </p>
                  <p className="font-display font-semibold text-[#2C3E50]">
                    {order.shipping.name}
                  </p>
                  <p className="text-sm text-[#2C3E50]/85 mt-1">
                    {order.shipping.address_line_1}
                    {order.shipping.address_line_2
                      ? `, ${order.shipping.address_line_2}`
                      : ""}
                    , {order.shipping.city}, {order.shipping.state} -{" "}
                    {order.shipping.pincode}
                  </p>
                  <p className="text-sm text-[#7F8C8D] mt-1">
                    Mobile: {order.shipping.mobile}
                  </p>
                </div>

                <div className="bg-[#FFFBF5] rounded-xl p-5 border border-[#F39C12]/15">
                  <p className="text-xs uppercase tracking-widest text-[#F39C12] font-semibold mb-3">
                    Items
                  </p>
                  <ul className="space-y-2">
                    {order.items.map((it) => (
                      <li
                        key={it.product_id}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-[#2C3E50]">
                          {it.name} × {it.quantity}
                        </span>
                        <span className="font-semibold">
                          ₹{(it.price * it.quantity).toLocaleString("en-IN")}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-3 border-t border-[#F39C12]/15 flex items-center justify-between">
                    <span className="text-sm text-[#7F8C8D]">Total</span>
                    <span className="font-display font-bold text-[#D63031]">
                      ₹{order.total.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <p className="text-xs text-[#7F8C8D] mt-1">
                    Payment: Cash on Delivery
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TrackOrder;
